import { useEffect, useLayoutEffect, useRef, useState } from 'react'

type Props = {
    /**
     * Encodes route + layout state (pathname, sidebar open/collapsed, which nav
     * items are present). A change re-measures the active item so the indicator
     * glides to its new position. Passed as a string so the effect has a stable,
     * exhaustive dependency instead of a spread array.
     */
    signature: string
}

type Target = { top: number; height: number; visible: boolean }

/**
 * The active-nav highlight — grey pill plus the red "tab" accent — rendered once
 * (as the nav's first child) and slid between items, rather than each item
 * painting its own. On a route change we measure the `[aria-current="page"]` item's
 * offset within the nav and move the indicator to it via a CSS transform+height
 * transition, so the accent glides from the old link to the new one instead of
 * snapping. Width follows the nav via left-3/right-3, so it tracks the rail
 * collapse/expand for free.
 */
export default function ActiveNavIndicator({ signature }: Props) {
    // Own ref, not the parent nav's: a child's layout effect runs before the
    // parent's ref is attached, so reading a nav ref here would be null on mount.
    // Our own ref is attached before our own effect, and parentElement is the nav.
    const selfRef = useRef<HTMLDivElement>(null)
    const [target, setTarget] = useState<Target>({ top: 0, height: 0, visible: false })
    // The first position is applied without a transition (so the accent doesn't
    // slide in from the top on load); transitions turn on after the first paint.
    const [animate, setAnimate] = useState(false)

    useLayoutEffect(() => {
        const nav = selfRef.current?.parentElement
        if (!nav) return

        const measure = () => {
            const active = nav.querySelector<HTMLElement>('[aria-current="page"]')
            // No active item on this route (e.g. a deep page not represented in
            // the nav): fade out rather than strand the accent on a stale item.
            if (!active) {
                setTarget((t) => (t.visible ? { ...t, visible: false } : t))
                return
            }
            const top = active.offsetTop
            const height = active.offsetHeight
            // Bail out on identical values, so re-measuring is free on the
            // frames where nothing moved.
            setTarget((t) =>
                t.top === top && t.height === height && t.visible
                    ? t
                    : { top, height, visible: true }
            )
        }

        measure()

        // One measurement is not enough: the rail's width is CSS-transitioned
        // (w-[68px] ⇄ w-60), so on expand this effect runs while the rail is
        // still narrow. The freshly mounted labels wrap at that width, every
        // item below them sits lower than it is about to, and a single
        // measurement strands the indicator where the active item USED to be.
        // Observing the nav re-measures as the transition plays out, so the
        // last measurement is of the settled layout — and it also tracks any
        // later reflow (a badge appearing, fonts loading, a window resize).
        // No feedback loop: the indicator is absolutely positioned, so moving
        // it never resizes the nav.
        const observer = new ResizeObserver(measure)
        observer.observe(nav)
        return () => observer.disconnect()
    }, [signature])

    useEffect(() => {
        setAnimate(true)
    }, [])

    return (
        <div
            ref={selfRef}
            aria-hidden="true"
            className={`pointer-events-none absolute left-3 right-3 top-0 z-0 rounded-md bg-ink-100 ${
                animate
                    ? 'transition-[transform,height,opacity] duration-200 ease-out motion-reduce:transition-none'
                    : ''
            }`}
            style={{
                transform: `translateY(${target.top}px)`,
                height: target.height,
                opacity: target.visible ? 1 : 0
            }}
        >
            {/* 2px red tab, matching the old before: bar's vertical inset. */}
            <span className="absolute bottom-2 left-0 top-2 w-0.5 rounded-full bg-arwen-600" />
        </div>
    )
}
